import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useChildStore } from '../store/childStore';
import { useAuthStore } from '../store/authStore';
import { PREBUILT_PATHWAYS, SAMPLE_LESSONS } from '../data/pathways';
import { GAMES } from '../data/games';
import { BADGES } from '../data/badges';
import type { BadgeDef } from '../data/badges';
import { SubjectBadge } from '../components/ui/Badge';
import { BadgeReveal } from '../components/badges/BadgeReveal';
import { LanguageToggle } from '../components/ui/LanguageToggle';
import { Mascot, MASCOT_NAMES } from '../components/mascots';
import type { MascotSubject } from '../components/mascots';
import { Avatar } from '../components/avatars/Avatar';
import { useT } from '../i18n'
import { useMascotMood } from '../hooks/useMascotMood';
import { PHASE_CONFIG } from '../types';
import { ConstellationMap } from '../components/constellation/ConstellationMap';
import { DailyWonder } from '../components/wonder/DailyWonder';
import { resolveTodayLesson, hasEarnedCompletionCelebration } from '../utils/nextLesson';
import { resolveActivePathway } from '../utils/activePathway';
import { SUBJECT_THEME, subjectTheme } from '../design/subjects';

// Subject colour comes from src/design/subjects.ts — the single source of truth.
// This page used to carry its own copy plus two more inline maps further down,
// which is how the tokens drifted out of sync with Badge.tsx and index.css.

// Stagger animation for child list items
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0 },
};

export default function ChildHome() {
  const navigate = useNavigate();
  const { activeChild, progress, earnedBadges } = useChildStore();
  const { user } = useAuthStore();
  const { t, lang, setLang } = useT()
  const mascotMood = useMascotMood();

  // ── Badge reveal ceremony ─────────────────────────────────────────────────
  const [revealBadge, setRevealBadge] = useState<BadgeDef | null>(null)

  const dismissReveal = useCallback(() => setRevealBadge(null), [])

  useEffect(() => {
    if (!activeChild?.id || !earnedBadges.length) return
    const key = `goodatoms_revealed_${activeChild.id}`
    const seen: string[] = JSON.parse(sessionStorage.getItem(key) ?? '[]')
    const pending = earnedBadges.find((eb) => !seen.includes(eb.id))
    if (!pending) return

    const def = BADGES.find((b) => b.id === pending.id)
    if (!def) return

    // Mark as seen before showing (prevents double-show on re-render)
    sessionStorage.setItem(key, JSON.stringify([...seen, pending.id]))
    // Short delay so the page settles before the overlay appears
    const id = setTimeout(() => setRevealBadge(def), 800)
    return () => clearTimeout(id)
  }, [activeChild?.id, earnedBadges])

  // Sync child's language preference to the global lang store on mount
  useEffect(() => {
    if (activeChild?.languagePreference) {
      setLang(activeChild.languagePreference);
    }
  }, [activeChild?.languagePreference, setLang]);

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? t('greeting.morning') :
    hour < 17 ? t('greeting.afternoon') :
    t('greeting.evening');

  const childName = activeChild?.name || user?.displayName || (lang === 'en' ? 'Explorer' : 'Utforsker');
  const avatar = activeChild?.avatar || '🌟';

  const activePathway = resolveActivePathway(activeChild);

  const currentProgress = activePathway ? progress[activePathway.id] : null;
  const completedIds = currentProgress?.lessonsCompleted || [];
  const allCompletedIds = Object.values(progress).flatMap((p) => p.lessonsCompleted);

  // Resolved against the real library, so a pathway whose lessons haven't been
  // authored yet falls back to something the child can actually open instead of
  // silently rendering the "everything finished" celebration.
  const todayLesson = resolveTodayLesson(
    activePathway,
    completedIds,
    allCompletedIds,
    activeChild?.ageBand,
  );
  const nextLessonId = todayLesson?.id;
  const showCompletionCelebration = hasEarnedCompletionCelebration(todayLesson, allCompletedIds);
  const streak = currentProgress?.streakDays || 0;
  const allPathways = PREBUILT_PATHWAYS;

  const subjectColors = todayLesson
    ? subjectTheme(todayLesson.subject)
    : SUBJECT_THEME.mathematics;

  // All explorable lessons (other than today's — for the "keep exploring" section)
  const exploreLessons = Object.values(SAMPLE_LESSONS).filter(
    (l) => l.id !== todayLesson?.id
  );

  return (
    <div className="min-h-screen bg-page">
      {/* Badge reveal ceremony — renders as fixed overlay above everything */}
      <BadgeReveal badge={revealBadge} onDismiss={dismissReveal} />

      <div className="max-w-4xl mx-auto px-5 py-10 space-y-10">

        {/* ── Header ─────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <Avatar id={avatar} size={64} />
            <div>
              <p className="text-ink/50 text-sm font-semibold">{greeting},</p>
              <h1 className="text-3xl font-extrabold text-ink leading-none">{childName}!</h1>
              {/* Phase badge */}
              {activeChild?.phase && (() => {
                const pc = PHASE_CONFIG[activeChild.phase];
                return (
                  <motion.span
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="inline-flex items-center gap-1 mt-1 text-xs font-semibold px-2.5 py-0.5 rounded-full border"
                    style={{ background: pc.bg, borderColor: pc.border, color: pc.color }}
                  >
                    {pc.emoji} {lang === 'no' ? pc.labelNo : pc.label}
                  </motion.span>
                );
              })()}
            </div>
            {/* Favorite mascot companion — bounces in after the greeting */}
            {activeChild?.favoriteMascot && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', delay: 0.7, stiffness: 300, damping: 20 }}
                className="shrink-0"
              >
                <Mascot
                  subject={activeChild.favoriteMascot as MascotSubject}
                  emotion={mascotMood}
                  size={56}
                />
              </motion.div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <LanguageToggle compact />

            {streak > 0 && (
              <motion.div
                initial={{ scale: 0, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', delay: 0.3, stiffness: 260, damping: 18 }}
                className="relative flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FEF3C7] border-2 border-[#FDE68A]"
              >
                {/* Milestone pulse ring for streaks ≥ 7 */}
                {streak >= 7 && (
                  <motion.div
                    aria-hidden="true"
                    className="absolute inset-0 rounded-full border-2 border-[#F59E0B]"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.7, 0, 0.7] }}
                    transition={{ duration: 1.8, repeat: Infinity }}
                  />
                )}
                <motion.span
                  animate={{
                    scale: [1, 1.3, 1],
                    rotate: streak >= 7 ? [0, -8, 8, 0] : 0,
                  }}
                  transition={{ duration: 0.8, repeat: Infinity, repeatDelay: streak >= 7 ? 1.5 : 2 }}
                  className="text-xl relative z-10"
                >
                  {streak >= 30 ? '👑' : streak >= 14 ? '💎' : streak >= 7 ? '🏆' : '🔥'}
                </motion.span>
                <span className="text-[#D97706] font-extrabold text-base relative z-10">
                  {streak} {t('streak.days')}
                </span>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* ── Screen-time reassurance ────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="flex items-center justify-center"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal/8 border border-teal/20 text-teal text-xs font-semibold">
            ⏱ {t('home.screen_time')}
          </span>
        </motion.div>

        {/* ── Daily Wonder ───────────────────────────── */}
        <DailyWonder />

        {/* ── Meet your crew ─────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.07 }}
        >
          <p className="text-ink/50 text-xs font-extrabold uppercase tracking-widest mb-5">
            {t('crew.label')}
          </p>
          <div className="flex items-end gap-6 overflow-x-auto pb-2 scrollbar-hide">
            {(['mathematics', 'science', 'technology', 'engineering', 'arts', 'nature'] as const).map((subj, i) => (
              <motion.div
                key={subj}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.06 }}
                className="flex flex-col items-center gap-2 shrink-0"
              >
                <Mascot subject={subj} emotion={mascotMood} size={76} />
                <span className="text-ink/50 text-xs font-extrabold tracking-wide">
                  {MASCOT_NAMES[subj]}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* ── Today's Lesson ─────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <p className="text-ink/50 text-xs font-extrabold uppercase tracking-widest mb-4">
            {t('today.label')}
          </p>

          {todayLesson ? (
            <div
              className={`relative rounded-3xl overflow-hidden bg-gradient-to-br ${subjectColors.gradient} border-2 shadow-card`}
              style={{ borderColor: subjectColors.border }}
            >
              {/* Decorative blobs */}
              <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full bg-white/50 blur-3xl pointer-events-none" aria-hidden="true" />
              <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-white/30 blur-2xl pointer-events-none" aria-hidden="true" />

              <div className="relative p-8 md:p-10 space-y-5">
                <div className="flex items-start justify-between">
                  <SubjectBadge subject={todayLesson.subject} />
                  <div className="flex items-center gap-3">
                    <span className="text-ink/50 text-sm font-bold bg-white/70 px-3 py-1 rounded-full">
                      ⏱ {todayLesson.durationMinutes} min
                    </span>
                    {/* Mascot as hero visual — floats in top-right */}
                    <Mascot subject={todayLesson.subject} emotion={mascotMood} size={80} />
                  </div>
                </div>

                <h2 className="text-2xl md:text-3xl font-extrabold text-ink leading-snug">
                  {lang === 'en' && todayLesson.titleEn ? todayLesson.titleEn : todayLesson.title}
                </h2>

                <p className="text-ink/60 text-base font-medium leading-relaxed line-clamp-2">
                  {lang === 'en' && todayLesson.saganHookEn ? todayLesson.saganHookEn : todayLesson.saganHook.content}
                </p>

                <div className="flex items-center gap-3 pt-1">
                  <motion.button
                    whileHover={{ scale: 1.05, boxShadow: '0 8px 24px rgba(0,194,168,0.35)' }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => navigate(`/lesson/${todayLesson.id}`)}
                    className="flex items-center gap-2 px-6 py-3 bg-teal rounded-2xl text-navy font-extrabold text-base shadow-glow cursor-pointer"
                  >
                    {t('today.start')}
                  </motion.button>
                  <div className="flex -space-x-1.5" aria-hidden="true">
                    {['🌀', '📖', '✏️', '🚀'].map((icon, i) => (
                      <div
                        key={i}
                        className="w-8 h-8 rounded-full bg-white border-2 border-white flex items-center justify-center text-sm shadow-sm"
                      >
                        {icon}
                      </div>
                    ))}
                  </div>
                  <span className="text-ink/50 text-sm font-semibold">{t('today.movements')}</span>
                </div>
              </div>
            </div>
          ) : showCompletionCelebration ? (
            /* Every available lesson genuinely finished — celebration is earned */
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="rounded-3xl bg-gradient-to-br from-teal/20 to-teal/5 border-2 border-teal/30 p-10 text-center space-y-4"
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                transition={{ duration: 0.9, repeat: 2 }}
                className="text-6xl"
                aria-hidden="true"
              >
                🎉
              </motion.div>
              <h2 className="text-2xl font-extrabold text-ink">{t('done.title')}</h2>
              <p className="text-ink/50 font-semibold">{t('done.sub')}</p>
            </motion.div>
          ) : (
            /* No lesson to offer and nothing completed — say so honestly rather
               than congratulating a child who hasn't started. */
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="rounded-3xl glass-card p-10 text-center space-y-4"
            >
              <div className="flex justify-center">
                <Mascot subject="nature" emotion="idle" size={72} />
              </div>
              <h2 className="text-2xl font-extrabold text-ink">{t('empty.title')}</h2>
              <p className="text-ink/60 font-semibold">{t('empty.sub')}</p>
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate('/library')}
                className="px-6 py-3 bg-teal rounded-2xl text-navy font-extrabold shadow-glow cursor-pointer"
              >
                {t('continue.explore')}
              </motion.button>
            </motion.div>
          )}
        </motion.div>

        {/* ── Constellation Knowledge Map ─────────────── */}
        {activePathway && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <ConstellationMap
              pathway={activePathway}
              progress={currentProgress}
              nextLessonId={nextLessonId}
            />
          </motion.div>
        )}

        {/* ── Badges ──────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <p className="text-ink/50 text-xs font-extrabold uppercase tracking-widest mb-4">
            {t('badges.label')}
          </p>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {BADGES.map((badge) => {
              const earned = earnedBadges.some((b) => b.id === badge.id);
              return (
                <motion.div
                  key={badge.id}
                  whileHover={{ scale: earned ? 1.08 : 1.03 }}
                  title={t(badge.nameKey)}
                  className={`flex-shrink-0 flex flex-col items-center gap-1.5 w-16 ${
                    earned ? 'opacity-100' : 'opacity-30 grayscale'
                  }`}
                >
                  <div className={`relative w-14 h-14 rounded-2xl flex items-center justify-center text-3xl ${
                    earned
                      ? 'bg-[#FEF3C7] border-2 border-[#FDE68A] shadow-sm'
                      : 'bg-ink/5 border-2 border-ink/10'
                  }`}>
                    {earned && (
                      <motion.div
                        aria-hidden="true"
                        className="absolute inset-0 rounded-2xl border-2 border-[#F59E0B]"
                        animate={{ scale: [1, 1.18, 1], opacity: [0.5, 0, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                    )}
                    <span className="relative z-10">{badge.icon}</span>
                  </div>
                  <p className="text-[10px] font-bold text-ink/60 text-center leading-tight line-clamp-2">
                    {t(badge.nameKey)}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* ── Keep Exploring ─────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-ink/50 text-xs font-extrabold uppercase tracking-widest mb-4">
            {t('explore.label')}
          </p>

          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            {/* Surprise me */}
            <motion.button
              variants={item}
              whileHover={{ y: -3, boxShadow: '0 8px 28px rgba(0,194,168,0.18)' }}
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                const ids = Object.keys(SAMPLE_LESSONS);
                navigate(`/lesson/${ids[Math.floor(Math.random() * ids.length)]}`);
              }}
              className="rounded-3xl glass-card hover:border-teal/40 transition-colors p-6 text-left flex items-center gap-4 group col-span-full"
            >
              <motion.div
                animate={{ rotate: [0, 18, -18, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 2 }}
                className="w-14 h-14 rounded-2xl bg-teal/10 border-2 border-teal/20 flex items-center justify-center text-3xl group-hover:scale-110 transition-transform shrink-0"
              >
                ✦
              </motion.div>
              <div className="flex-1">
                <div className="font-extrabold text-ink text-lg">{t('explore.surprise')}</div>
                <div className="text-ink/50 text-sm font-medium mt-0.5">
                  {t('explore.surprise_sub')}
                </div>
              </div>
              <div className="text-2xl text-ink/50 group-hover:text-teal transition-colors">→</div>
            </motion.button>

            {/* Explorable lessons */}
            {exploreLessons.map((lesson) => {
              const colors = subjectTheme(lesson.subject);
              return (
                <motion.button
                  key={lesson.id}
                  variants={item}
                  whileHover={{ y: -3, boxShadow: '0 8px 28px rgba(30,30,46,0.12)' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate(`/lesson/${lesson.id}`)}
                  className="rounded-3xl p-6 text-left border-2 space-y-3 flex flex-col"
                  style={{ backgroundColor: colors.surface, borderColor: colors.border }}
                >
                  <div className="flex items-center justify-between">
                    <Mascot subject={lesson.subject} size={52} />
                    <SubjectBadge subject={lesson.subject} />
                  </div>
                  <h3 className="font-extrabold text-ink text-base leading-snug line-clamp-2">
                    {lang === 'en' && lesson.titleEn ? lesson.titleEn : lesson.title}
                  </h3>
                  <p className="text-ink/50 text-sm font-medium line-clamp-2 leading-relaxed">
                    {(lang === 'en' && lesson.saganHookEn ? lesson.saganHookEn : lesson.saganHook.content).slice(0, 80)}...
                  </p>
                  <div className="flex items-center gap-2 mt-auto pt-2">
                    <span className="text-xs font-bold text-ink/50">⏱ {lesson.durationMinutes} min</span>
                    <span className="ml-auto text-sm font-extrabold" style={{ color: colors.ink }}>
                      {t('explore.start')}
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </motion.div>
        </motion.div>

        {/* ── Interactive Games ─────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-ink/50 text-xs font-extrabold uppercase tracking-widest mb-4">
            {t('games.label')}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {GAMES.filter(g => g.ageBands.includes(activeChild?.ageBand ?? '8-12')).slice(0, 6).map((game, i) => {
              const theme = subjectTheme(game.subject);
              const accent = theme.accent;
              return (
                <motion.button
                  key={game.id}
                  whileHover={{ y: -3, boxShadow: '0 8px 28px rgba(30,30,46,0.12)' }}
                  whileTap={{ scale: 0.97 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.06 }}
                  onClick={() => navigate(`/game/${game.id}`)}
                  className="rounded-2xl p-4 text-left border-2 space-y-2"
                  style={{ backgroundColor: theme.surface, borderColor: accent + '30' }}
                >
                  <span className="text-3xl">{game.emoji}</span>
                  <p className="font-extrabold text-ink text-sm leading-tight">{lang === 'en' ? (game.titleEn ?? game.title) : game.title}</p>
                  <p className="text-xs text-ink/60">{lang === 'en' ? (game.descriptionEn ?? game.description) : game.description}</p>
                </motion.button>
              );
            })}
          </div>
          <motion.button
            whileHover={{ x: 4 }}
            onClick={() => navigate('/library')}
            className="mt-3 text-sm font-bold text-teal hover:underline"
          >
            {t('games.all')}
          </motion.button>
        </motion.div>

        {/* ── Available Pathways ─────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
        >
          <p className="text-ink/50 text-xs font-extrabold uppercase tracking-widest mb-4">
            {t('pathways.label')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {allPathways.map((pathway, i) => {
              const colors = subjectTheme(pathway.subject);
              const isActive = pathway.id === activePathway?.id;
              return (
                <motion.button
                  key={pathway.id}
                  whileHover={{ y: -3, boxShadow: '0 8px 28px rgba(30,30,46,0.12)' }}
                  whileTap={{ scale: 0.97 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45 + i * 0.08 }}
                  onClick={() => navigate(`/lesson/${pathway.lessonIds[0]}`)}
                  className={`rounded-3xl p-6 text-left border-2 bg-gradient-to-br ${colors.gradient} ${isActive ? 'ring-2 ring-teal ring-offset-2' : ''} space-y-3`}
                  style={{ borderColor: colors.border }}
                >
                  <div className="flex items-center justify-between">
                    <Mascot subject={pathway.subject} size={48} />
                    {isActive && (
                      <span className="text-xs font-extrabold text-teal bg-teal/10 px-2 py-0.5 rounded-full border border-teal/20">
                        {t('pathways.active')}
                      </span>
                    )}
                  </div>
                  <SubjectBadge subject={pathway.subject} />
                  <h3 className="font-extrabold text-ink text-base leading-snug">
                    {lang === 'en' ? (pathway.titleEn ?? pathway.title) : pathway.title}
                  </h3>
                  <p className="text-ink/50 text-sm font-semibold">
                    {pathway.totalLessons} {t('pathways.lessons')}
                  </p>
                </motion.button>
              );
            })}
          </div>
        </motion.div>

      </div>
    </div>
  );
}
